import { useNavigate } from "react-router";
import { useWalletKit } from "@web3jskit/walletkit";
import { Wallet, ShieldCheck, RefreshCw } from "lucide-react";
import { useState, useEffect } from "react";
import { toast, Toaster } from "sonner";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import Logo from "../../imports/Logo-1/Logo-134-42";
import { hasValidChainLogoUrl } from "../components/NetworkInfoCard";
import { LoginBackground } from "../components/LoginBackground";
import { LoginStagePrefs } from "../components/LoginStagePrefs";
import { LoginWordmark } from "../components/LoginWordmark";
import { LoginCapabilityWall } from "../components/login-tiles/LoginCapabilityWall";
import { useLogin } from "../hooks/useLogin";
import { getWalletErrorMessageKey } from "@/utils/walletError";
import { useIsAuthenticated } from "../../stores/modules/auth";
import {
  useDepositBlockchainsStore,
  selectPrimaryBlockchain,
} from "../../stores/modules/depositBlockchains";
import "./login-stage.css";

export function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const { theme } = useTheme();
  const { login, resetWallet, getLastWalletErrorCode } = useLogin();
  const { walletAddress } = useWalletKit();
  const isAuthenticated = useIsAuthenticated();

  const primaryChain = useDepositBlockchainsStore(selectPrimaryBlockchain);
  const chainStatus = useDepositBlockchainsStore((s) => s.status);
  const chainErrorMessage = useDepositBlockchainsStore((s) => s.errorMessage);
  const refreshBlockchains = useDepositBlockchainsStore((s) => s.refreshBlockchains);

  const [chainLogoFailed, setChainLogoFailed] = useState(false);

  useEffect(() => {
    setChainLogoFailed(false);
  }, [primaryChain?.chainLogoUrl]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    document.title = t("layout.docTitle");
    document.documentElement.lang = i18n.resolvedLanguage || "en";
  }, [t, i18n.language, i18n.resolvedLanguage]);

  const chainReady = chainStatus === "success" && primaryChain !== null;
  const chainCardLoading = chainStatus === "idle" || chainStatus === "loading";
  const chainCardError = chainStatus === "error";
  const connectDisabled =
    isConnecting ||
    !chainReady ||
    chainCardLoading ||
    chainCardError ||
    (chainStatus === "success" && !primaryChain);

  const displayWallet =
    walletAddress && walletAddress.length >= 10
      ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
      : walletAddress;
  const resetDisabled = isConnecting || isResetting;

  const handleResetWallet = async () => {
    if (resetDisabled) return;
    setIsResetting(true);
    try {
      await resetWallet();
    } catch (err) {
      console.error("[Login] Reset wallet failed:", err);
    } finally {
      setIsResetting(false);
    }
  };

  const handleConnect = async () => {
    if (connectDisabled) return;
    setIsConnecting(true);
    try {
      const result = await login();

      if (!result) {
        const code = getLastWalletErrorCode();
        toast.error(
          code && code !== "unknown"
            ? t(getWalletErrorMessageKey(code))
            : t("login.toastFailed")
        );
        return;
      }

      toast.success(t("login.toastConnected"));
      navigate("/");
    } catch (err) {
      console.error("[Login] Connection failed:", err);
      toast.error(t("login.toastFailed"));
    } finally {
      setIsConnecting(false);
    }
  };

  const showChainRemoteLogo =
    primaryChain != null &&
    hasValidChainLogoUrl(primaryChain.chainLogoUrl) &&
    !chainLogoFailed;

  return (
    <div className="login-stage relative min-h-screen overflow-hidden">
      <LoginBackground />
      <div className="login-grain" aria-hidden />
      <LoginStagePrefs />
      <Toaster
        position="top-center"
        theme={theme as "light" | "dark" | "system"}
        toastOptions={{
          className:
            "border border-slate-200 dark:border-slate-800 shadow-none bg-slate-50 dark:bg-[#1a1d1f] text-slate-900 dark:text-slate-200",
        }}
      />

      <div className="relative z-10 flex min-h-screen flex-col md:h-screen md:flex-row">
        <div className="order-2 min-h-0 min-w-0 flex-1 p-4 md:order-1 md:h-full md:p-7 md:pr-5">
          <LoginCapabilityWall />
        </div>

        <main className="login-rail login-rise order-1 relative min-h-0 w-full overflow-auto border-b border-[var(--lg-line)] bg-[var(--lg-rail)] px-7 py-10 md:order-2 md:h-full md:border-b-0 md:border-l md:px-10 flex flex-col justify-center">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-[var(--lg-mint)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 right-0 h-56 w-56 rounded-full bg-[var(--lg-mint)] opacity-15 blur-3xl"
          />

          <LoginWordmark label={t("layout.brand")} />

          <div className="relative mt-5 flex h-20 w-20 items-center justify-center rounded-2xl border border-[var(--lg-line)] bg-[var(--lg-bg)] p-3.5">
            <Logo className="h-full w-full" />
          </div>

          <h1 className="login-display relative mt-7 text-[2.15rem] font-extrabold leading-[0.95] tracking-tight text-[var(--lg-ink)]">
            {t("login.consoleTitle")}
          </h1>
          <p className="relative mt-4 max-w-sm text-[15px] leading-relaxed text-[var(--lg-mute)]">
            {t("login.subtitle")}
          </p>

          <div className="relative mt-5 flex flex-wrap gap-2">
            {[t("login.chipSign"), t("login.chipKeys"), t("login.chipBilling")].map((chip) => (
              <span
                key={chip}
                className="login-mono rounded-full border border-[var(--lg-line)] bg-[var(--lg-bg)] px-2.5 py-1 text-[10px] font-medium tracking-[0.12em] uppercase text-[var(--lg-ink)]"
              >
                {chip}
              </span>
            ))}
          </div>

          <div className="relative mt-8 space-y-4">
            <div className="flex min-h-[92px] items-center gap-4 rounded-2xl border border-[var(--lg-line)] bg-[var(--lg-bg)] p-4">
              {chainCardLoading ? (
                <>
                  <div className="h-11 w-11 shrink-0 animate-pulse rounded-xl border border-[var(--lg-line)] bg-[var(--lg-line)]" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 animate-pulse rounded-full bg-[var(--lg-line)]" />
                    <div className="h-3 w-full max-w-[220px] animate-pulse rounded-full bg-[var(--lg-line)]" />
                  </div>
                </>
              ) : chainCardError ? (
                <div className="flex w-full flex-col gap-3">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {chainErrorMessage || t("login.chainLoadFailed")}
                  </p>
                  <button
                    type="button"
                    onClick={() => void refreshBlockchains({ force: true })}
                    className="login-mono self-start text-xs font-semibold tracking-[0.12em] uppercase text-[var(--lg-mint)] underline underline-offset-4"
                  >
                    {t("login.chainRetry")}
                  </button>
                </div>
              ) : chainReady ? (
                <>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--lg-line)] bg-[var(--lg-rail)]">
                    {showChainRemoteLogo ? (
                      <img
                        src={primaryChain.chainLogoUrl.trim()}
                        alt=""
                        className="h-6 w-6 rounded-full object-contain"
                        onError={() => setChainLogoFailed(true)}
                      />
                    ) : (
                      <div className="h-6 w-6 animate-pulse rounded-full bg-[var(--lg-line)]" aria-hidden />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-[var(--lg-mint)]" />
                      <p className="truncate text-sm font-semibold">{primaryChain.chainName}</p>
                    </div>
                    <p className="mt-1 truncate text-xs text-[var(--lg-mute)]">
                      {walletAddress ? (
                        <span className="login-mono font-medium tracking-normal text-[var(--lg-ink)]">
                          {displayWallet}
                        </span>
                      ) : (
                        t("login.signatureHint")
                      )}
                    </p>
                  </div>
                  {walletAddress ? (
                    <button
                      type="button"
                      onClick={() => void handleResetWallet()}
                      disabled={resetDisabled}
                      className="login-mono inline-flex shrink-0 cursor-pointer items-center gap-1.5 text-xs font-semibold tracking-[0.12em] uppercase text-[var(--lg-mute)] transition-colors hover:text-[var(--lg-mint)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${isResetting ? "animate-spin" : ""}`} />
                      {t("login.reset")}
                    </button>
                  ) : (
                    <ShieldCheck className="h-5 w-5 shrink-0 text-[var(--lg-mint)]" />
                  )}
                </>
              ) : (
                <p className="text-sm text-[var(--lg-mute)]">{t("login.noChainsAvailable")}</p>
              )}
            </div>

            <button
              type="button"
              onClick={handleConnect}
              disabled={connectDisabled}
              className="group relative flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border-0 bg-[var(--lg-mint)] px-4 py-3.5 text-sm font-bold text-[var(--lg-mint-ink)] transition-colors duration-200 enabled:hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lg-mint)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--lg-rail)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isConnecting ? (
                <>
                  <svg
                    className="mr-1 h-5 w-5 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3.738l3-2.647z"
                    />
                  </svg>
                  {t("login.connecting")}
                </>
              ) : (
                <>
                  <Wallet className="h-5 w-5" />
                  {t("login.connectWallet")}
                </>
              )}
            </button>

            <p className="login-mono flex items-center justify-center gap-2 text-[10px] tracking-[0.14em] uppercase text-[var(--lg-mute)]">
              <ShieldCheck className="h-3.5 w-3.5 text-[var(--lg-mint)]" />
              {t("login.noPasswordHint")}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
